import { createLatestReadGate } from './latestReadGate.js'
import { schedulePayload } from './dispatchScheduleForm.js'

export function scheduleState() {
  return { items: [], reading: false, writing: false, readError: '', writeError: '', saved: '',
    pending: null, editorOpen: false, source: null, latestSource: null, fieldErrors: {}, schedulerEnabled: false }
}

export function createScheduleController({ state, api, tenant, project, kind, current, saved = () => {} }) {
  const reads = createLatestReadGate()
  const scope = `${tenant}:${project}:${kind}`
  const matches = (row, request) => request.id
    ? row.id === request.id && row.last_operation_key === request.body.operation_key
    : row.create_key === request.body.create_key
  function accept(receipt, request) {
    if (!current()) return
    if (!receipt?.id || !Number.isInteger(receipt.revision) || receipt.revision < 1 || !matches(receipt, request)) {
      throw new Error('保存回执不完整，请重新读取日程确认。')
    }
    reads.invalidate()
    const previous = state.items.find(row => row.id === receipt.id)
    const merged = { ...(previous || {}), ...receipt }
    if (previous?.last_occurrence && receipt.last_occurrence_id !== previous.last_occurrence.id) merged.last_occurrence = null
    const others = state.items.filter(row => row.id !== receipt.id)
    state.items = [merged, ...others]
    state.pending = null
    state.editorOpen = false
    state.writeError = ''
    state.latestSource = null
    state.fieldErrors = {}
    state.saved = '日程已保存。'
    state.reading = false
    saved(receipt)
  }
  async function read() {
    if (!current()) return
    const ticket = reads.begin(scope)
    state.reading = true
    try {
      const response = await api.list(tenant, project, { kind })
      if (!current() || !reads.accepts(ticket, scope)) return
      if (!Array.isArray(response?.items)) throw new Error('日程列表暂时无法读取。')
      state.items = response.items
      state.schedulerEnabled = response.scheduler_enabled === true
      state.readError = ''
      const latest = state.source && state.items.find(row => row.id === state.source.id)
      state.latestSource = latest && latest.revision !== state.source.revision ? latest : null
      if (state.pending) {
        const receipt = state.items.find(row => matches(row, state.pending))
        if (receipt) accept(receipt, state.pending)
      }
    } catch (error) {
      if (current() && reads.accepts(ticket, scope)) state.readError = error?.message || '日程列表暂时无法读取。'
    } finally {
      if (current() && reads.accepts(ticket, scope)) state.reading = false
    }
  }
  async function reconcile(request) {
    const response = await api.list(tenant, project, request.id ? { kind } : { create_key: request.body.create_key })
    if (!current()) return false
    const receipt = (response?.items || []).find(row => matches(row, request))
    if (!receipt) return false
    accept(receipt, request)
    return true
  }
  async function save(draft, source = null) {
    if (!current() || state.writing) return
    const content = schedulePayload(draft)
    const hash = JSON.stringify({ id: source?.id || null, revision: source?.revision || null, content })
    if (state.pending && state.pending.hash !== hash) {
      state.writeError = '上一条保存请求还未确认，请先重新读取；输入已保留。'
      return
    }
    const request = state.pending || { id: source?.id || null, hash, body: { ...content,
      ...(source ? { expected_revision: source.revision, operation_key: crypto.randomUUID() }
        : { create_key: crypto.randomUUID() }) } }
    return send(request)
  }
  async function send(request) {
    if (!current() || state.writing) return
    state.pending = request
    state.writing = true
    state.writeError = ''
    state.fieldErrors = {}
    state.saved = ''
    reads.invalidate()
    try {
      const receipt = request.id ? await api.update(tenant, project, request.id, request.body)
        : await api.create(tenant, project, request.body)
      if (current()) accept(receipt, request)
    } catch (error) {
      if (!current()) return
      const status = Number(error?.status || error?.statusCode || 0)
      if (status >= 400 && status < 500) {
        state.pending = null
        state.fieldErrors = error?.details?.fields || (Array.isArray(error?.details)
          ? Object.fromEntries(error.details.map(item => [item.loc?.[1], item.msg])) : {})
        state.writeError = error?.message || '请核对日程字段后保存。'
      } else {
        try { if (await reconcile(request)) return } catch { /* Preserve the unresolved write and its original key. */ }
        if (current()) state.writeError = '保存结果尚未确认，输入已保留。请重新读取，或用原请求重试。'
      }
    } finally { state.writing = false }
  }
  return { read, save, retryPending: () => state.pending && send(state.pending), dispose: () => reads.invalidate() }
}
